import { Box, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useIntl } from 'react-intl'
import { formatLocalDate } from '../../../app/utils'
import { messages } from '../messages'
import { UnassignedConsignment, JourneyStatusTypeId, JourneyStatusTypeLabels } from '../types'

type JourneyViewRouteCardProps = {
  route: any
  routeNumber: number
  consignments: UnassignedConsignment[]
}

const JourneyViewRouteCard = ({
  route,
  routeNumber,
  consignments,
}: JourneyViewRouteCardProps) => {
  const { formatMessage } = useIntl()

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-'
    return formatLocalDate(dateString)
  }

  const getActionTypeName = (actionTypeId: number): string => {
    switch (actionTypeId) {
      case 1:
        return formatMessage(messages.pickup)
      case 2:
        return formatMessage(messages.dropoff)
      default:
        return '-'
    }
  }

  const getConsignmentTrackingNumber = (loadId: number): string => {
    for (const consignment of consignments) {
      if (consignment.loads) {
        const load = consignment.loads.find((l) => l.loadId === loadId)
        if (load) {
          return load.trackingNumber || consignment.trackingNumber || '-'
        }
      }
    }
    return '-'
  }

  const getStatusChipColor = (status?: string): 'default' | 'warning' | 'success' | 'info' | 'error' => {
    if (!status) return 'default'
    const lowerStatus = status.toLowerCase()
    if (lowerStatus.includes('scheduled') || lowerStatus.includes('planned')) return 'warning'
    if (lowerStatus.includes('progress')) return 'info'
    if (lowerStatus.includes('completed')) return 'success'
    if (lowerStatus.includes('cancelled')) return 'error'
    return 'default'
  }

  const routeStatus = route.status || JourneyStatusTypeLabels[JourneyStatusTypeId.Planned]

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        minWidth: 400,
        flex: '1 1 400px',
        maxWidth: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {formatMessage(messages.routeNumber, { number: routeNumber })}
        </Typography>
        {route.carrierName && (
          <Chip
            label={route.carrierName}
            size="small"
            sx={{
              bgcolor: 'grey.700',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        )}
        <Chip
          label={routeStatus}
          size="small"
          color={getStatusChipColor(routeStatus)}
        />
      </Box>

      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
              {formatMessage(messages.destination)}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
              {formatMessage(messages.date)}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
              {formatMessage(messages.action)}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
              {formatMessage(messages.service)}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
              {formatMessage(messages.loads)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {route.destinations && route.destinations.length > 0 ? (
            route.destinations.map((destination: any) => {
              // API returns 'loadActions' not 'shipmentBookingActions'
              const loadActions = destination.loadActions || destination.shipmentBookingActions || []
              
              if (loadActions.length === 0) {
                return (
                  <TableRow key={destination.id}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {destination.location?.name || destination.locationName || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {formatDate(destination.scheduledDate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>-</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>-</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>-</TableCell>
                  </TableRow>
                )
              }

              return loadActions.map((action: any, actionIndex: number) => (
                <TableRow key={`${destination.id}-${action.loadId}-${actionIndex}`}>
                  {actionIndex === 0 ? (
                    <>
                      <TableCell
                        sx={{ fontSize: '0.75rem' }}
                        rowSpan={loadActions.length}
                      >
                        {destination.location?.name || destination.locationName || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: '0.75rem' }}
                        rowSpan={loadActions.length}
                      >
                        {formatDate(destination.scheduledDate)}
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {getActionTypeName(action.destinationActionTypeId)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {(() => {
                      // Services are now stored in destination.loadServices with service.name
                      const loadServices = destination.loadServices || []
                      const servicesForLoad = loadServices.filter(
                        (s: any) => s.loadId === action.loadId
                      )
                      return servicesForLoad.length > 0
                        ? servicesForLoad.map((s: any) => s.service?.name || s.serviceId).join(', ')
                        : '-'
                    })()}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {getConsignmentTrackingNumber(action.loadId)}
                  </TableCell>
                </TableRow>
              ))
            })
          ) : (
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: 'center', fontSize: '0.75rem' }}>
                {formatMessage(messages.noDestinations)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

export default JourneyViewRouteCard
